import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  Users,
  Wallet,
  Inbox,
  ArrowUpRight,
  Sparkles,
  Loader2,
} from "lucide-react";
import AnimatedCounter from "@/components/AnimatedCounter";
import { fetchAnalytics, fetchCampaigns, fetchAllCreators } from "@/lib/api";
import { formatFollowers, formatINR } from "@/lib/format";

const ACTIVITY = [
  { time: "2h ago", text: "Priya K. approved 'Nykaa Monsoon Glow' final reel from @priya.styles" },
  { time: "5h ago", text: "New lead: Plum (₹5L–₹10L) added to CRM" },
  { time: "Yesterday", text: "Campaign 'boAt Wireless Launch' marked Completed" },
  { time: "Yesterday", text: "Rohan M. updated rate card to ₹1.8L/long-form" },
  { time: "2d ago", text: "Proposal sent to Bewakoof for Festive Collection campaign" },
  { time: "2d ago", text: "Invoice ₹14L raised against Nykaa" },
];

const StageBadge = ({ stage }) => {
  const map = {
    Active: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    Completed: "bg-blue-500/10 text-blue-300 border-blue-500/30",
    "Proposal Sent": "bg-amber-500/10 text-amber-300 border-amber-500/30",
    "Deliverables Pending": "bg-orange-500/10 text-orange-300 border-orange-500/30",
    Negotiation: "bg-purple-500/10 text-purple-300 border-purple-500/30",
    Paid: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30",
    Lead: "bg-zinc-500/10 text-zinc-400 border-zinc-700",
  };
  return (
    <span
      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
        map[stage] || "bg-zinc-500/10 text-zinc-300 border-zinc-700"
      }`}
    >
      {stage}
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, value, delta, prefix, suffix, decimals, testId }) => (
  <div
    className="rounded-xl border border-white/8 bg-white/[0.02] p-5 hover:border-blue-500/30 hover:bg-blue-500/[0.03] transition-all"
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <div className="h-9 w-9 rounded-lg bg-blue-600/10 border border-blue-500/20 flex items-center justify-center">
        <Icon className="h-4 w-4 text-blue-400" />
      </div>
      <span className="text-[11px] inline-flex items-center gap-1 text-emerald-400">
        <TrendingUp className="h-3 w-3" /> {delta}
      </span>
    </div>
    <div className="mt-4 text-2xl font-semibold tracking-tighter">
      <AnimatedCounter
        value={value}
        prefix={prefix || ""}
        suffix={suffix || ""}
        decimals={decimals || 0}
      />
    </div>
    <div className="text-xs uppercase tracking-wider text-zinc-500 mt-1.5">{label}</div>
  </div>
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [topCreators, setTopCreators] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [a, c, cr] = await Promise.all([fetchAnalytics(), fetchCampaigns(), fetchAllCreators()]);
        setData(a);
        setCampaigns(c);
        setTopCreators([...cr].sort((x, y) => y.er - x.er).slice(0, 5));
      } catch (_e) {
        setData(false);
      }
    })();
  }, []);

  if (data === null) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-500" data-testid="dashboard-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const thisWeek = campaigns.slice(0, 5);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>
        <p className="text-sm text-zinc-500 mt-1">
          A live view of your agency operations and pipeline.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Sparkles} label="Active Campaigns" value={kpis.activeCampaigns || 0} delta="+1 wk" testId="kpi-active-campaigns" />
        <KpiCard icon={Users} label="Creators in DB" value={kpis.creatorsCount || 0} delta="+12" testId="kpi-creators" />
        <KpiCard icon={Wallet} label="Revenue This Month" value={kpis.revenueThisMonthLakh || 0} prefix="₹" suffix="L" delta="+18%" testId="kpi-revenue" />
        <KpiCard icon={Inbox} label="Leads in Pipeline" value={kpis.leadsInPipeline || 0} delta="+4" testId="kpi-leads" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-1 rounded-xl border border-white/8 bg-white/[0.02] p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold tracking-tight">Recent Activity</h2>
            <Link to="/app/campaigns" className="text-xs text-blue-400 hover:text-blue-300">View all</Link>
          </div>
          <ul className="mt-4 space-y-3" data-testid="activity-feed">
            {ACTIVITY.map((a, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
                <div>
                  <p className="text-zinc-200 leading-snug">{a.text}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{a.time}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="lg:col-span-2 rounded-xl border border-white/8 bg-white/[0.02] p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold tracking-tight">Campaigns This Week</h2>
            <Link to="/app/campaigns" className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1">
              All campaigns <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="campaigns-this-week-table">
              <thead className="text-[11px] uppercase tracking-wider text-zinc-500">
                <tr className="text-left">
                  <th className="py-2 font-medium">Campaign</th>
                  <th className="py-2 font-medium">Brand</th>
                  <th className="py-2 font-medium">Creators</th>
                  <th className="py-2 font-medium">Stage</th>
                  <th className="py-2 font-medium">Due</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {thisWeek.map((c) => (
                  <tr key={c.id} className="hover:bg-white/[0.02]">
                    <td className="py-3">
                      <Link to={`/app/campaigns/${c.id}`} className="text-zinc-100 hover:text-blue-300">{c.name}</Link>
                    </td>
                    <td className="py-3 text-zinc-300">{c.brand}</td>
                    <td className="py-3 text-zinc-300">{c.creatorCount}</td>
                    <td className="py-3"><StageBadge stage={c.stage} /></td>
                    <td className="py-3 text-zinc-400">{c.endDate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold tracking-tight">Top Performing Creators</h2>
          <Link to="/app/creators" className="text-xs text-blue-400 hover:text-blue-300">View all</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="top-creators-table">
            <thead className="text-[11px] uppercase tracking-wider text-zinc-500">
              <tr className="text-left">
                <th className="py-2 font-medium">Creator</th>
                <th className="py-2 font-medium">Platform</th>
                <th className="py-2 font-medium">Followers</th>
                <th className="py-2 font-medium">ER</th>
                <th className="py-2 font-medium">Last Campaign</th>
                <th className="py-2 font-medium">Rate Card</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {topCreators.map((c) => (
                <tr key={c.id} className="hover:bg-white/[0.02]">
                  <td className="py-3">
                    <Link to={`/app/creators/${c.id}`} className="flex items-center gap-2 text-zinc-100 hover:text-blue-300">
                      <img src={c.avatar} alt="" className="h-7 w-7 rounded-full object-cover" />
                      {c.name}
                    </Link>
                  </td>
                  <td className="py-3 text-zinc-300">{c.primaryPlatform}</td>
                  <td className="py-3 text-zinc-300">{formatFollowers(c.followers)}</td>
                  <td className="py-3 text-blue-300">{c.er}%</td>
                  <td className="py-3 text-zinc-400">{c.history?.[0]?.brand || "—"}</td>
                  <td className="py-3 text-zinc-300">{formatINR(c.rateCard?.reel || 0)}/reel</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
